/**
 * Tool commands (W2-11 batch-tools; DESIGN.md §5.8; WORKPACKAGES Appendix B `tools.distribute|clone|fill|path|bake|
 * flatten|stagger`, `edit.pasteSpecial`). They open the generator sheets with the current selection (each sheet runs
 * its tool in a live gesture); Stagger and Paste special act directly.
 *
 *  - `tools.fill`: two selected notes open the sheet; RPE-style picking also works: `{ pick: 'head' }` remembers the
 *    selected note as the start (RPE Ctrl+F), `{ pick: 'tail' }` opens the sheet with it and the selected end note
 *    (RPE Ctrl+G).
 *  - `tools.flatten`: `{ line, prop }` (Channel view, contracted) or the focused column / selected segments' property;
 *    otherwise a choice among the active line's properties that have several layers.
 *  - `tools.stagger`: shift each selected item by an increasing step (palette prompt for the step, else a choice).
 *  - `edit.pasteSpecial` (Ctrl+Shift+V): the Paste special dialog; the lane's paste flow (W2-03 `edit.paste`) then
 *    performs the choice (DialogMap.pasteSpecial, typed `{ special }` arguments): a ghost at the mouse, or an
 *    immediate paste at the playhead / the original beats.
 */

import { DIALOG, type PasteSpecialResult } from '../../app/dialogs/types';
import type { BeatArg, CommandArgs, PasteModeArg } from '../../app/registry/commands';
import type { CommandContext, CommandDef } from '../../app/registry/types';
import { LAYERED_PROPS, isLineProp } from '../../core/model/props';
import type { LineId, LineProp, NoteId } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import { formatBeat } from '../../core/time/beatSyntax';
import type { Beat } from '../../core/time/types';
import { Tools } from '../../core/tools/index';
import type { DocStore } from '../../state/types';
import { m } from './batch.messages';
import { playheadBeat, ref, selectionIds, transactWithToasts, workingLine, type Enabled } from './batchContext';
import { PROP_LABEL } from './sheets/FlattenSheet';
import { selectedSegments } from './segmentOps';
import { cancelTopSheet, openToolSheet, sheetOpen } from './sheetRegistry';

function obj(args: unknown): Readonly<Record<string, unknown>> {
  return typeof args === 'object' && args !== null && !Array.isArray(args) ? (args as Record<string, unknown>) : {};
}

function projectOpen(ctx: CommandContext): Enabled {
  return ctx.store === null ? ref(m.reasonNoProject) : true;
}

// ---------------------------------------------------------------------------------------------------------------
// Fill picks (RPE Ctrl+F / Ctrl+G)
// ---------------------------------------------------------------------------------------------------------------

const fillHeads = new WeakMap<DocStore, NoteId>();

function fillPair(ctx: CommandContext): readonly [NoteId, NoteId] | null {
  const store = ctx.store;
  if (store === null) return null;
  const notes = selectionIds(ctx).notes;
  if (notes.length === 2) return [notes[0] as NoteId, notes[1] as NoteId];
  const head = fillHeads.get(store);
  const tail = notes[0];
  if (notes.length === 1 && head !== undefined && tail !== undefined && head !== tail && store.doc.notes.has(head)) {
    return [head, tail];
  }
  return null;
}

async function openFill(ctx: CommandContext, pair: readonly [NoteId, NoteId]): Promise<void> {
  const line = ctx.store?.doc.notes.get(pair[0])?.line ?? null;
  await openToolSheet(ctx, DIALOG.fill, { notes: [...pair], segments: [], line });
}

// ---------------------------------------------------------------------------------------------------------------
// Flatten target
// ---------------------------------------------------------------------------------------------------------------

function layerCount(store: DocStore, line: LineId, prop: LineProp): number {
  return store.idx.tracksOfLine(line).get(prop)?.length ?? 0;
}

function flattenTarget(ctx: CommandContext): { line: LineId; prop: LineProp } | null {
  const store = ctx.store;
  if (store === null) return null;
  const col = ctx.editor.getState().focusedColumn;
  if (col !== null && LAYERED_PROPS.includes(col.prop as never) && layerCount(store, col.line, col.prop) >= 2) {
    return { line: col.line, prop: col.prop };
  }
  for (const id of selectedSegments(ctx)) {
    const k = store.doc.keys.get(id);
    const owner = k === undefined ? undefined : store.doc.tracks.get(k.track)?.owner;
    if (owner?.kind === 'line' && layerCount(store, owner.line, owner.prop) >= 2)
      return { line: owner.line, prop: owner.prop };
  }
  return null;
}

/** True when some line has a property with two or more layers. */
function anyFlattenable(store: DocStore): boolean {
  const seen = new Set<string>();
  for (const t of store.doc.tracks.values()) {
    if (t.owner.kind !== 'line') continue;
    const key = `${String(t.owner.line)}:${t.owner.prop}`;
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
}

function flattenCandidates(ctx: CommandContext): LineProp[] {
  const store = ctx.store;
  const line = workingLine(ctx);
  if (store === null || line === null) return [];
  return LAYERED_PROPS.filter((p) => layerCount(store, line, p) >= 2);
}

// ---------------------------------------------------------------------------------------------------------------
// Stagger
// ---------------------------------------------------------------------------------------------------------------

const STAGGER_STEPS: readonly Beat[] = [32, 16, 8, 4, 2, 1].map((d) => Beats.make(0, 1, d));

function beatArg(args: unknown): Beat | null {
  const a = obj(args);
  const raw = (a.step ?? args) as Partial<BeatArg> | undefined;
  if (raw === undefined || typeof raw !== 'object') return null;
  const { w, n, d } = raw;
  if (typeof w !== 'number' || typeof n !== 'number' || typeof d !== 'number') return null;
  try {
    return Beats.make(w, n, d);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Paste special
// ---------------------------------------------------------------------------------------------------------------

function toBeatArg(b: Beat): BeatArg {
  return { w: b.w, n: b.n, d: b.d };
}

/**
 * `edit.paste` arguments that hand a paste special choice to the lane's paste flow (`{ special: {...} }`,
 * CommandArgs['edit.paste']): the mode, the anchor ('mouse', or an exact beat), the target line and the X offset,
 * which the lane keeps next to its ghost (so M mirrors the notes and leaves the offset as chosen).
 */
export function pasteHandoffArgs(
  r: NonNullable<PasteSpecialResult>,
  anchor: 'mouse' | Beat,
): CommandArgs['edit.paste'] {
  const { every, ...rest } = r.mode;
  const mode: PasteModeArg = every === undefined ? rest : { ...rest, every: toBeatArg(every) };
  return {
    special: {
      mode,
      anchor: anchor === 'mouse' ? 'mouse' : toBeatArg(anchor),
      targetLine: r.targetLine,
      xShift: r.xShift,
    },
  };
}

async function pasteSpecial(ctx: CommandContext): Promise<void> {
  const store = ctx.store;
  if (store === null) return;
  const payload = await ctx.clipboard.read();
  if (payload === null) {
    ctx.ui.toast(ref(m.clipboardEmpty), { kind: 'info' });
    return;
  }
  const fallback = payload.lines[0]?.sourceLine;
  const targetLine =
    workingLine(ctx) ?? (fallback !== undefined && store.doc.lines.has(fallback) ? fallback : store.doc.lineOrder[0]);
  if (targetLine === undefined) {
    ctx.ui.toast(ref(m.reasonNoLine), { kind: 'error' });
    return;
  }
  const r = await ctx.ui.openDialog(DIALOG.pasteSpecial, { payload, targetLine });
  if (r === null || ctx.store !== store) return;
  // The playhead goes over as its beat, so "Playhead" pastes there at once, as the dialog's summary says.
  const anchor = r.anchor === 'playhead' ? playheadBeat(ctx) : r.anchor;
  // The dialog only chooses (DialogMap.pasteSpecial): the lane's paste flow performs the choice (ghost or immediate,
  // selection, locked lines, "paste the rest" for add values) and keeps the X offset next to its ghost.
  await ctx.registry.run('edit.paste', pasteHandoffArgs(r, anchor), ctx.source);
}

// ---------------------------------------------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------------------------------------------

export const TOOL_COMMANDS: readonly CommandDef[] = [
  {
    id: 'tools.distribute',
    title: m.cmdDistribute,
    category: 'Tools',
    icon: 'WandSparkles',
    aliases: ['ramp', 'distribute', 'MultiNote', 'MultiEvent', 'batch edit', 'rand', '批量编辑'],
    level: 'guided',
    enabled(ctx) {
      if (ctx.store === null) return ref(m.reasonNoProject);
      const ids = selectionIds(ctx);
      return ids.notes.length + ids.keyish.length > 0 ? true : ref(m.distributeNeedsItems);
    },
    async run(ctx) {
      const ids = selectionIds(ctx);
      // Keys selected as points travel in `keys` (GeneratorSheetProps, CR-W2-11-2), segments by their start key.
      await openToolSheet(ctx, DIALOG.distribute, {
        notes: ids.notes,
        segments: ids.segments,
        keys: ids.keys,
        line: workingLine(ctx),
      });
    },
    menus: [
      { menu: 'tools.menu', group: 'generate', order: 10 },
      { menu: 'lane.context', group: 'tools', order: 10 },
      { menu: 'motion.context', group: 'tools', order: 10 },
    ],
  },
  {
    id: 'tools.clone',
    title: m.cmdClone,
    category: 'Tools',
    icon: 'Copy',
    aliases: ['clone', 'clone to lines', 'RPE clone', '克隆'],
    level: 'full',
    enabled(ctx) {
      if (ctx.store === null) return ref(m.reasonNoProject);
      const ids = selectionIds(ctx);
      return selectedSegments(ctx).length + ids.notes.length > 0 ? true : ref(m.cloneNeedsSource);
    },
    async run(ctx) {
      const ids = selectionIds(ctx);
      await openToolSheet(ctx, DIALOG.clone, {
        notes: ids.notes,
        segments: selectedSegments(ctx),
        line: workingLine(ctx),
      });
    },
    menus: [
      { menu: 'tools.menu', group: 'generate', order: 20 },
      { menu: 'motion.context', group: 'tools', order: 20 },
      { menu: 'lane.context', group: 'tools', order: 20 },
    ],
  },
  {
    id: 'tools.fill',
    title: m.cmdFill,
    category: 'Tools',
    icon: 'Spline',
    aliases: ['curve notes', 'fill between', 'RPE curve', '曲线填充'],
    level: 'guided',
    enabled(ctx) {
      if (ctx.store === null) return ref(m.reasonNoProject);
      return selectionIds(ctx).notes.length > 0 ? true : ref(m.fillNeedTwo);
    },
    async run(ctx, args) {
      const store = ctx.store;
      if (store === null) return;
      const pickWhich = obj(args).pick;
      const notes = selectionIds(ctx).notes;
      if (pickWhich === 'head') {
        const head = notes[0];
        if (notes.length !== 1 || head === undefined) {
          ctx.ui.toast(ref(m.fillPickOne), { kind: 'warning' });
          return;
        }
        fillHeads.set(store, head);
        // No toast here (UX-047): `UiService.toast` has no dismiss handle (CR-W3-05-3.3), so a "start remembered"
        // toast outlives a fast head-then-tail pick and sits over the open Fill sheet. The hint bar carries both the
        // confirmation and the next step instead, and the tail run below clears it as the step completes.
        ctx.ui.setGestureHints([
          { chord: '', label: ref(m.fillHeadSet) },
          { chord: 'Ctrl+KeyG', label: ref(m.fillPickTailHint) },
        ]);
        return;
      }
      const pair = fillPair(ctx);
      if (pair === null) {
        ctx.ui.toast(ref(pickWhich === 'tail' ? m.fillPickHeadFirst : m.fillNeedTwo), { kind: 'warning' });
        return;
      }
      if (fillHeads.has(store)) ctx.ui.setGestureHints(null);
      fillHeads.delete(store);
      await openFill(ctx, pair);
    },
    menus: [
      { menu: 'tools.menu', group: 'generate', order: 30 },
      { menu: 'lane.context', group: 'tools', order: 30 },
    ],
  },
  {
    id: 'tools.path',
    title: m.cmdPath,
    category: 'Tools',
    icon: 'Route',
    aliases: ['complex move', 'motion path', 'circle', 'orbit', '复杂移动'],
    level: 'full',
    enabled: (ctx) =>
      ctx.store === null ? ref(m.reasonNoProject) : workingLine(ctx) === null ? ref(m.reasonNoLine) : true,
    async run(ctx) {
      await openToolSheet(ctx, DIALOG.motionPath, {
        notes: [],
        segments: selectedSegments(ctx),
        line: workingLine(ctx),
      });
    },
    menus: [
      { menu: 'tools.menu', group: 'generate', order: 40 },
      { menu: 'motion.context', group: 'tools', order: 40 },
      { menu: 'preview.context', group: 'tools', order: 40 },
    ],
  },
  {
    id: 'tools.bake',
    title: m.cmdBake,
    category: 'Tools',
    icon: 'Scissors',
    aliases: ['bake', 'cut', 'simplify', 'fit', '拟合', '切割'],
    level: 'full',
    enabled: (ctx) =>
      ctx.store === null ? ref(m.reasonNoProject) : selectedSegments(ctx).length > 0 ? true : ref(m.reasonNoSegments),
    async run(ctx, args) {
      await openToolSheet(ctx, DIALOG.bake, {
        notes: [],
        segments: selectedSegments(ctx),
        line: workingLine(ctx),
        ...(obj(args).mode === 'simplify' ? { mode: 'simplify' as const } : {}),
      });
    },
    menus: [
      { menu: 'tools.menu', group: 'motion', order: 10 },
      { menu: 'motion.context', group: 'tools', order: 30 },
    ],
  },
  {
    id: 'tools.flatten',
    title: m.cmdFlatten,
    category: 'Tools',
    icon: 'Layers',
    aliases: ['merge layers', 'flatten layers', '合并层'],
    level: 'full',
    enabled(ctx, args) {
      if (ctx.store === null) return ref(m.reasonNoProject);
      // With arguments (Channel view "Flatten to layer 0") the given property must have layers to flatten; without
      // them, run picks the focused column or selection and explains when there is none.
      const a = obj(args);
      if (typeof a.line === 'number' && typeof a.prop === 'string' && isLineProp(a.prop)) {
        return ctx.store.doc.lines.has(a.line as LineId) && layerCount(ctx.store, a.line as LineId, a.prop) >= 2
          ? true
          : ref(m.flattenNothing);
      }
      return anyFlattenable(ctx.store) ? true : ref(m.flattenNothing);
    },
    async run(ctx, args) {
      const a = obj(args);
      const store = ctx.store;
      if (store === null) return;
      let target: { line: LineId; prop: LineProp } | null = null;
      if (
        typeof a.line === 'number' &&
        typeof a.prop === 'string' &&
        isLineProp(a.prop) &&
        store.doc.lines.has(a.line as LineId)
      ) {
        target = { line: a.line as LineId, prop: a.prop };
      }
      target ??= flattenTarget(ctx);
      if (target === null) {
        const line = workingLine(ctx);
        const props = flattenCandidates(ctx);
        if (line === null || props.length === 0) {
          ctx.ui.toast(ref(m.flattenNothing), { kind: 'warning' });
          return;
        }
        const picked =
          props.length === 1
            ? props[0]
            : await ctx.ui.choose<LineProp>({
                title: ref(m.flattenChoose),
                options: props.map((p) => ({ value: p, label: ref(PROP_LABEL[p]) })),
              });
        if (picked === null || picked === undefined) return;
        target = { line, prop: picked };
      }
      await openToolSheet(ctx, DIALOG.flatten, target);
    },
    menus: [
      { menu: 'tools.menu', group: 'motion', order: 20 },
      { menu: 'motion.context', group: 'tools', order: 50 },
      { menu: 'curve.context', group: 'tools', order: 50 },
    ],
  },
  {
    id: 'tools.stagger',
    title: m.cmdStagger,
    category: 'Tools',
    icon: 'ChevronsRight',
    aliases: ['stagger', 'offset each', 'cascade'],
    level: 'full',
    args: { kind: 'beat', label: ref(m.staggerStep) },
    enabled(ctx) {
      if (ctx.store === null) return ref(m.reasonNoProject);
      const ids = selectionIds(ctx);
      return ids.notes.length + ids.keyish.length >= 2 ? true : ref(m.staggerNeedsTwo);
    },
    async run(ctx, args) {
      let step = beatArg(args);
      if (step === null) {
        const picked = await ctx.ui.choose<string>({
          title: ref(m.staggerStep),
          body: ref(m.staggerBody),
          options: STAGGER_STEPS.map((b) => ({
            value: Beats.key(b),
            label: ref(m.staggerOption, { beat: formatBeat(b) }),
          })),
        });
        if (picked === null) return;
        step = STAGGER_STEPS.find((b) => Beats.key(b) === picked) ?? null;
        if (step === null) return;
      }
      const ids = selectionIds(ctx);
      const s = step;
      transactWithToasts(
        ctx,
        { label: ref(m.txnStagger), command: 'tools.stagger' },
        (tx) => Tools.stagger(tx, { notes: ids.notes, keys: ids.keyish, step: s }),
        (r) => ref(m.staggerDone, { count: r.changed, beat: formatBeat(s) }),
      );
    },
    menus: [
      { menu: 'tools.menu', group: 'timing', order: 10 },
      { menu: 'lane.context', group: 'tools', order: 40 },
      { menu: 'motion.context', group: 'tools', order: 60 },
    ],
  },
  {
    id: 'tools.closeSheet',
    title: m.cmdCloseSheet,
    category: 'Tools',
    icon: 'X',
    aliases: ['close sheet', 'cancel tool'],
    level: 'guided',
    enabled: () => (sheetOpen() ? true : ref(m.reasonNoSheet)),
    run(ctx) {
      // Focus inside the sheet: the sheet's own Escape handling already closes it.
      const active = typeof document === 'undefined' ? null : document.activeElement;
      if (active?.closest('[data-batch-sheet], [role="dialog"]') != null) return;
      cancelTopSheet(ctx.ui);
    },
  },
  {
    id: 'edit.pasteSpecial',
    title: m.cmdPasteSpecial,
    category: 'Edit',
    icon: 'ClipboardPaste',
    aliases: ['paste special', 'paste options', 'stamp', 'repeat paste'],
    level: 'guided',
    enabled: projectOpen,
    async run(ctx) {
      await pasteSpecial(ctx);
    },
    menus: [
      { menu: 'app.edit', group: 'clipboard', order: 40 },
      { menu: 'lane.context', group: 'clipboard', order: 40 },
      { menu: 'motion.context', group: 'clipboard', order: 40 },
    ],
  },
];
